import { useEffect, useRef } from 'react';
import { gsap } from '../lib/gsapSetup';
import SectionTag from '../components/SectionTag';
import { useReducedMotion, useIsDesktop } from '../hooks/useMotionPrefs';

// Abstract network nodes — intentionally unlabelled until verified
// Amaya Energy locations/markets are supplied. No invented geography.
const NODES = [
  { x: 300, y: 430 },
  { x: 560, y: 300 },
  { x: 760, y: 390 },
  { x: 990, y: 270 },
  { x: 1180, y: 420 },
];

const ARCS = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [1, 3],
];

function arcPath(a, b) {
  const mx = (a.x + b.x) / 2;
  const my = Math.min(a.y, b.y) - 90;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
}

export default function GlobalNetwork() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (reduced) {
      ref.current.querySelectorAll('.net-arc').forEach((p) => (p.style.strokeDashoffset = 0));
      ref.current.querySelectorAll('.net-node').forEach((n) => (n.style.opacity = 1));
      return;
    }
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: ref.current,
          start: 'top 65%',
          toggleActions: 'play none none none',
        },
      });
      tl.timeScale(0.5);
      tl.fromTo('.net-bg', { scale: 1.12 }, { scale: 1, ease: 'none', duration: 1 }, 0)
        // one point appears, then another, then the network forms
        .fromTo('.net-node-0', { opacity: 0, scale: 0 }, { opacity: 1, scale: 1, duration: 0.06 }, 0.05)
        .fromTo('.net-node-1', { opacity: 0, scale: 0 }, { opacity: 1, scale: 1, duration: 0.06 }, 0.22)
        .to('.net-arc-0', { strokeDashoffset: 0, duration: 0.12, ease: 'none' }, 0.28)
        .fromTo('.net-node-2', { opacity: 0, scale: 0 }, { opacity: 1, scale: 1, duration: 0.06 }, 0.4)
        .to('.net-arc-1', { strokeDashoffset: 0, duration: 0.12, ease: 'none' }, 0.46)
        .fromTo('.net-node-3', { opacity: 0, scale: 0 }, { opacity: 1, scale: 1, duration: 0.06 }, 0.56)
        .to('.net-arc-2', { strokeDashoffset: 0, duration: 0.1, ease: 'none' }, 0.6)
        .to('.net-arc-4', { strokeDashoffset: 0, duration: 0.1, ease: 'none' }, 0.66)
        .fromTo('.net-node-4', { opacity: 0, scale: 0 }, { opacity: 1, scale: 1, duration: 0.06 }, 0.72)
        .to('.net-arc-3', { strokeDashoffset: 0, duration: 0.12, ease: 'none' }, 0.76)
        .fromTo('.net-caption', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.14 }, 0.85);
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="global"
      data-testid="global-network-section"
      className="relative h-screen overflow-hidden bg-[#000000]"
    >
      <img
        src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=1920&auto=format&fit=crop"
        alt="Earth at night — global networks of light"
        loading="lazy"
        decoding="async"
        className="net-bg absolute inset-0 h-full w-full object-cover opacity-50 will-change-transform"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />

      <div className="absolute left-5 top-24 z-10 md:left-12 md:top-28">
        <SectionTag number="09" label="Global Scale" />
        <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight text-white md:text-4xl">
          A network, forming
        </h2>
      </div>

      <svg viewBox={isDesktop ? '0 0 1440 700' : '200 80 1060 560'} preserveAspectRatio={isDesktop ? 'xMidYMid slice' : 'xMidYMid meet'} className="absolute inset-0 h-full w-full" aria-hidden="true">
        {ARCS.map(([a, b], i) => (
          <path
            key={i}
            className={`net-arc net-arc-${i} energy-glow`}
            d={arcPath(NODES[a], NODES[b])}
            fill="none"
            stroke="rgba(71,160,217,0.75)"
            strokeWidth="1.4"
            pathLength={1}
            strokeDasharray={1}
            strokeDashoffset={1}
          />
        ))}
        {NODES.map((n, i) => (
          <circle key={i} className={`net-node net-node-${i} node-dot`} cx={n.x} cy={n.y} r="5" fill="#FFB000" />
        ))}
      </svg>

      <p className="net-caption absolute bottom-10 left-5 max-w-sm text-[11px] uppercase tracking-[0.22em] text-white/45 md:left-12">
        One network, expanding — region by region
      </p>
    </section>
  );
}
